import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Link,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDeviceDocuments } from "../hooks/useDeviceDocuments";
import {
  createDocument,
  updateDocument,
  deleteDocument,
} from "../api/documents";

const DeviceDocuments = ({
  deviceId,
  user,
  autoLoad = true,
  showDownload = false,
  alwaysExpanded = false,
}) => {
  const { documents, loading, error, loadDocuments, refreshDocuments } =
    useDeviceDocuments(deviceId);
  const [expanded, setExpanded] = useState(alwaysExpanded);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [formData, setFormData] = useState({
    document_name: "",
    document_type: "",
    document_url: "",
    mime_type: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (autoLoad && deviceId) {
      loadDocuments();
    }
  }, [deviceId, autoLoad, loadDocuments]);

  const handleAccordionChange = (event, isExpanded) => {
    if (alwaysExpanded) {
      return;
    }
    setExpanded(isExpanded);
    // Load documents when accordion is first expanded if not auto-loaded
    if (isExpanded && !autoLoad && documents.length === 0 && !loading) {
      loadDocuments();
    }
  };

  const formatDocumentDate = (dateString) => {
    if (!dateString) return "Unknown date";
    return new Date(dateString).toLocaleDateString();
  };

  const getDocumentIcon = (documentName) => {
    const extension = documentName?.split(".").pop()?.toLowerCase();
    return (
      <DescriptionIcon color={extension === "pdf" ? "error" : "primary"} />
    );
  };

  const truncateUrl = (url, maxLength = 50) => {
    if (!url) return "No URL provided";
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + "...";
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleAddDocument = () => {
    setEditingDoc(null);
    setFormData({
      document_name: "",
      document_type: "manual",
      document_url: "",
      mime_type: "",
      notes: "",
    });
    setModalOpen(true);
  };

  const handleEditDocument = (doc) => {
    setEditingDoc(doc);
    setFormData({
      document_name: doc.document_name || "",
      document_type: doc.document_type || "manual",
      document_url: doc.document_url || "",
      mime_type: doc.mime_type || "",
      notes: doc.notes || "",
    });
    setModalOpen(true);
  };

  const handleDeleteDocument = async (docId, docName) => {
    if (
      window.confirm(`Are you sure you want to delete the link to"${docName}"?`)
    ) {
      try {
        setSubmitting(true);
        const result = await deleteDocument(docId);
        if (result.success) {
          showSnackbar("Document deleted successfully");
          refreshDocuments();
        } else {
          showSnackbar(result.error || "Failed to delete document", "error");
        }
      } catch (error) {
        showSnackbar(`Error: ${error.message}`, "error");
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleSubmitDocument = async () => {
    try {
      setSubmitting(true);

      const documentData = {
        ...formData,
        device_id: deviceId,
        uploaded_by: user.username, // passed as prop
      };

      let result;
      if (editingDoc) {
        result = await updateDocument({
          ...documentData,
          pk_id: editingDoc.pk_id,
        });
      } else {
        result = await createDocument(documentData);
      }

      if (result.success) {
        showSnackbar(
          `Document ${editingDoc ? "updated" : "created"} successfully`,
        );
        setModalOpen(false);
        refreshDocuments();
      } else {
        showSnackbar(
          result.error ||
            `Failed to ${editingDoc ? "update" : "create"} document`,
          "error",
        );
      }
    } catch (error) {
      showSnackbar(`Error: ${error.message}`, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingDoc(null);
  };

  if (!deviceId) {
    return null;
  }

  return (
    <Card variant="outlined" size={{ sm: 2, md: 3 }} sx={{ mt: 3 }}>
      <Accordion
        expanded={alwaysExpanded || expanded}
        onChange={handleAccordionChange}
      >
        <AccordionSummary
          expandIcon={alwaysExpanded ? null : <ExpandMoreIcon />}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.03)",
            cursor: alwaysExpanded ? "default" : "pointer",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.05)",
            },
          }}
        >
          {/* AccordionSummary renders a <button>, so its content must stay inline elements. */}
          <Box
            component="span"
            sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
          >
            <DescriptionIcon color="primary" />
            <Typography variant="h6" component="span">
              Documents
            </Typography>
            <Chip
              component="span"
              label={loading ? "Loading..." : documents.length}
              size="small"
              variant="outlined"
              color={documents.length > 0 ? "primary" : "default"}
            />
            <Box
              component="span"
              sx={{ ml: "auto", mr: 1, display: "flex", gap: 1 }}
            >
              <Tooltip title="Add document">
                <IconButton
                  component="span"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddDocument();
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh documents">
                <IconButton
                  component="span"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    refreshDocuments();
                  }}
                  disabled={loading}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <CardContent sx={{ pt: 0 }}>
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {error && !loading && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!loading && !error && documents.length === 0 && (
              <Alert severity="info">No documents found for this device.</Alert>
            )}

            {!loading && !error && documents.length > 0 && (
              <List disablePadding>
                {documents.map((doc, index) => (
                  <ListItem
                    key={doc.pk_id || index}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      mb: 1,
                      "&:last-child": { mb: 0 },
                    }}
                    secondaryAction={
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        {showDownload && (
                          <Tooltip title="Download document">
                            <IconButton edge="end" size="small">
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit document">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleEditDocument(doc)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete document">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() =>
                              handleDeleteDocument(doc.pk_id, doc.document_name)
                            }
                            color="error"
                            disabled={submitting}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getDocumentIcon(doc.document_name)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <>
                          <Typography variant="subtitle2">
                            {doc.document_name || "Untitled Document"}
                          </Typography>
                          {doc.document_url ? (
                            <Tooltip title={doc.document_url} placement="top">
                              <Link
                                href={
                                  doc.document_url.startsWith("http")
                                    ? doc.document_url
                                    : `https://${doc.document_url}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="caption"
                                color="primary"
                                sx={{
                                  display: "block",
                                  textDecoration: "none",
                                  "&:hover": {
                                    textDecoration: "none",
                                  },
                                }}
                              >
                                🔗 {truncateUrl(doc.document_url)}
                              </Link>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              No URL provided
                            </Typography>
                          )}
                        </>
                      }
                      secondary={
                        <Box>
                          {doc.notes && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {doc.notes}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Created: {formatDocumentDate(doc.created_at)}
                            {doc.updated_at &&
                              doc.updated_at !== doc.created_at &&
                              ` • Updated: ${formatDocumentDate(doc.updated_at)}`}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </AccordionDetails>
      </Accordion>

      {/* Document Add/Edit Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingDoc ? "Edit Document" : "Add New Document"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Document Name"
              value={formData.document_name}
              onChange={handleFormChange("document_name")}
              fullWidth
              required
            />
            <TextField
              label="Document Type"
              value={formData.document_type}
              onChange={handleFormChange("document_type")}
              select
              fullWidth
            >
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="warranty">Warranty</MenuItem>
              <MenuItem value="specification">Specification</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
            <TextField
              label="Document URL"
              value={formData.document_url}
              onChange={handleFormChange("document_url")}
              fullWidth
              placeholder="https://..."
            />
            <TextField
              label="MIME Type"
              value={formData.mime_type}
              onChange={handleFormChange("mime_type")}
              fullWidth
              placeholder="application/pdf, image/png, etc."
            />
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={handleFormChange("notes")}
              fullWidth
              multiline
              rows={3}
              placeholder="Additional notes about this document..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            onClick={handleSubmitDocument}
            variant="contained"
            disabled={submitting || !formData.document_name.trim()}
          >
            {submitting ? "Saving..." : editingDoc ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default DeviceDocuments;
